/**
 * KAN-826: section "Свои поля" of the list upload form. Each row: field name,
 * file column, where to store it (organization or contact) and whether the
 * value is passed to the bot. Row messages come from the client-side check,
 * the list under the section from the server 400 (`extra_fields`).
 */
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { MAX_EXTRA_FIELDS, TARGET_OPTIONS, type ExtraFieldDraft } from '@/lib/uploadCompanyFields';
import type { ExtraFieldMapping, ExtraFieldTarget } from '@/types/dataset';
import { SectionErrors } from './SectionErrors';
import { UploadColumnPicker } from './UploadColumnPicker';

interface UploadExtraFieldsProps {
  rows: ExtraFieldDraft[];
  headers: string[];
  onAdd: () => void;
  onChange: (id: string, patch: Partial<ExtraFieldMapping>) => void;
  onRemove: (id: string) => void;
  rowErrors?: Record<string, string>;
  errors?: string[];
}

const targetOptions = TARGET_OPTIONS.map((option) => ({ ...option }));

export function UploadExtraFields({
  rows,
  headers,
  onAdd,
  onChange,
  onRemove,
  rowErrors = {},
  errors = [],
}: UploadExtraFieldsProps): JSX.Element {
  const full = rows.length >= MAX_EXTRA_FIELDS;
  return (
    <div data-testid="upload-extra-fields" className="bg-white border rounded-lg p-4 space-y-4">
      <div>
        <h3 className="text-base font-medium text-gray-900">Свои поля</h3>
        <p className="text-sm text-gray-500">
          Другие колонки файла: сохраняются в организации или в контакте.
        </p>
      </div>
      {rows.map((row, index) => {
        const error = rowErrors[row.id];
        return (
          <div
            key={row.id}
            data-testid={`extra-row-${index}`}
            className="space-y-2 rounded-md border border-gray-100 p-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-center">
              <Input
                data-testid={`extra-name-${index}`}
                aria-label="Название поля"
                placeholder="Название поля"
                value={row.name}
                aria-invalid={error ? true : undefined}
                onChange={(e) => onChange(row.id, { name: e.target.value })}
              />
              <UploadColumnPicker
                id={`extra-column-${row.id}`}
                selectTestId={`extra-column-${index}`}
                inputTestId={`extra-column-input-${index}`}
                ariaLabel="Колонка"
                emptyLabel="Выберите колонку"
                value={row.column}
                onChange={(column) => onChange(row.id, { column })}
                headers={headers}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                data-testid={`extra-remove-${index}`}
                aria-label="Удалить поле"
                onClick={() => onRemove(row.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <NativeSelect
                data-testid={`extra-target-${index}`}
                aria-label="Куда записать"
                className="h-9 w-40"
                value={row.target}
                onChange={(e) => onChange(row.id, { target: e.target.value as ExtraFieldTarget })}
                options={targetOptions}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <Checkbox
                  data-testid={`extra-to-bot-${index}`}
                  checked={row.to_bot}
                  onCheckedChange={(checked) => onChange(row.id, { to_bot: checked })}
                />
                Передавать боту
              </label>
            </div>
            {error && (
              <p data-testid={`extra-row-error-${index}`} className="text-xs text-red-600">
                {error}
              </p>
            )}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={onAdd} disabled={full}>
          <Plus className="h-4 w-4 mr-2" />
          Добавить поле
        </Button>
        {full && (
          <span className="text-xs text-gray-500">Своих полей не больше {MAX_EXTRA_FIELDS}.</span>
        )}
      </div>
      <SectionErrors testId="extra-fields-error" errors={errors} />
    </div>
  );
}
